/**
 * Default receiver invitation copy, per language.
 *
 * The backend is the source of truth: it serves the same map on the organization-info
 * endpoint (`receiver_invitation_message_templates`, built from
 * `internal/htmltemplate/email_translations.go`) and renders the invitation from it when
 * a message is actually sent. The map below is only a fallback for the window before org
 * info has loaded — keep it in sync with the backend translations.
 */
export const DEFAULT_INVITE_MESSAGE_TEMPLATES: Record<string, string> = {
  en: "{{.OrganizationName}} sent you funds. Download {wallet} to create your account and access your funds: {{.RegistrationLink}}",
  es: "{{.OrganizationName}} te ha enviado fondos. Descarga {wallet} para crear tu cuenta y acceder a tus fondos: {{.RegistrationLink}}",
  fr: "{{.OrganizationName}} vous a envoyé des fonds. Téléchargez {wallet} pour créer votre compte et accéder à vos fonds : {{.RegistrationLink}}",
  ht: "{{.OrganizationName}} voye lajan ba ou. Telechaje {wallet} pou kreye kont ou epi jwenn lajan ou: {{.RegistrationLink}}",
  ny: "{{.OrganizationName}} yakutumizani ndalama. Tsitsani {wallet} kuti mupange akaunti ndikupeza ndalama zanu: {{.RegistrationLink}}",
  tum: "{{.OrganizationName}} yamutumizgirani ndalama. Khazikitsani {wallet} kuti mupange akaunti ndikusanga ndalama zinu: {{.RegistrationLink}}",
  uk: "{{.OrganizationName}} надіслав вам кошти. Завантажте {wallet}, щоб створити обліковий запис та отримати доступ до коштів: {{.RegistrationLink}}",
};

export const DEFAULT_LANGUAGE = "en";
export const DEFAULT_WALLET_NAME = "VIA Wallet";

/**
 * Returns the default invitation copy for `language`, with the `{wallet}` placeholder
 * resolved. `languageTemplates` is the map served by the backend; the bundled copy is
 * used when it is not available yet. Falls back to English for unknown languages.
 */
export const getDefaultInviteMessage = ({
  language,
  walletName,
  languageTemplates,
}: {
  language?: string;
  walletName?: string;
  languageTemplates?: Record<string, string>;
}): string => {
  const templates =
    languageTemplates && Object.keys(languageTemplates).length > 0
      ? languageTemplates
      : DEFAULT_INVITE_MESSAGE_TEMPLATES;

  const template =
    (language ? templates[language] : undefined) ??
    templates[DEFAULT_LANGUAGE] ??
    DEFAULT_INVITE_MESSAGE_TEMPLATES[DEFAULT_LANGUAGE];

  return template.replace(/\{wallet\}/g, walletName || DEFAULT_WALLET_NAME);
};

/**
 * Resolves the invitation copy a receiver will actually get, mirroring the precedence
 * the backend applies when it sends the message (see `prepareMessage` in
 * `internal/services/send_receiver_wallets_invite_service.go`):
 *
 * 1. the disbursement's own custom template, when set;
 * 2. the organization-level custom template, when set;
 * 3. the default copy for the disbursement's language.
 */
export const resolveInviteMessage = ({
  disbursementMessage,
  organizationMessage,
  language,
  walletName,
  languageTemplates,
}: {
  disbursementMessage?: string;
  organizationMessage?: string;
  language?: string;
  walletName?: string;
  languageTemplates?: Record<string, string>;
}): string =>
  disbursementMessage?.trim() ||
  organizationMessage?.trim() ||
  getDefaultInviteMessage({ language, walletName, languageTemplates });
